import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { mapActions, mapGetters } from 'vuex'

import * as types from '@webparts/store/types'
import { CalendarEventResponseData, CalendarFolderInfo, CalendarDayInfo, CalendarEvent, BirthdayEmployeeInfo, CalendarEventColor, VacationerInfo } from './types'

import api from '@webparts/components/webparts/YASKAWA/Calendar/api'
import CalendarDay from './CalendarDay.vue'
import { IEDate, uGetDateFormat, uGetDaysInMonth, getDaysArray, uGetZero } from '@appbase/util/date-helper'


@Component({
    components: {
        CalendarDay
    },
    computed: mapGetters({
        monthlyEvents: types.WEBPART_CALENDARMONTHLYEVENT_DATA,
        folders: types.WEBPART_CALENDARFOLDER_DATA
    }),
    methods: {
        ...mapActions({
            getMonthlyEvents: types.WEBPART_CALENDARMONTHLYEVENT_DATA
        })
    }
})
export default class Calendar extends Vue {
    getMonthlyEvents!: (payload: { start: Date, end: Date }) => Promise<CalendarEventResponseData[]>

    monthlyEvents!: CalendarEventResponseData[];
    folders!: CalendarFolderInfo[];

    monthNames: Array<string> = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ]

    //
    // ────────────────────────────────────────────────── I ──────────
    //   :::::: P R O P S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────
    //

    @Prop({ type: String }) value!: string
    @Prop({ type: Array }) weeks!: Array<string>
    @Prop({ type: Boolean, default: false }) preview!: boolean

    //
    // ──────────────────────────────────────────────── I ──────────
    //   :::::: D A T A : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────
    //

    MonthMatrix: Array<Array<CalendarDayInfo>> = []
    selectedEvent: Array<CalendarEvent> = []

    matrixStart: Date = new Date()
    matrixEnd: Date = new Date()

    //
    // ──────────────────────────────────────────────────────── I ──────────
    //   :::::: C O M P U T E D : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────────────
    //

    get date(): Date {
        return IEDate(this.value)
    }

    get YearMonthDateFormat() {
        const monthName = this.monthNames[this.date.getMonth() - 1]
        return uGetDateFormat(`${monthName} yyyy`, this.date)
    }   

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: W A T C H E S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //

    @Watch('value')
    onValueChanged(newVal: string, oldVal: string) {
        const oldDate = IEDate(oldVal)
        const newDate = IEDate(newVal)
        const newMonth = newDate.getMonth()
        const newDay = newDate.getDate()
        if (oldDate.getMonth() !== newMonth) {
            this.calcMatrix(true)
        } else {
            for (var row of this.MonthMatrix) {
                for (var day of row) {
                    if (day.selected) day.selected = false
                    if (day.month === newMonth && day.day === newDay)
                        day.selected = true
                }
            }
        }
    }

    @Watch('monthlyEvents')
    onEventsChanged(newVal: CalendarEventResponseData[]) {    
        //일정: 오렌지, 휴가:그린, 생일: 블루        
        const colorEvent: CalendarEventColor = { code: 'orange', category: 'event', hasColor: false }
        // const colorVacation: CalendarEventColor = { code: 'green', category: 'vacation', hasColor: false }
        // const colorBirthDay: CalendarEventColor = { code: 'blue', category: 'birthDay', hasColor: false }
        
        this.clearMatrix();

        newVal.forEach(item => {
            let start = IEDate(item.start);
            let end = IEDate(item.end);

            let days: CalendarDayInfo[] = [];
            if (this.isOneDayEvent(item)) {
              days.push(this.findDay(start)!);
            } else {
              days = this.findDays(start, end);
            }

            days.forEach((day, index) => {
                if (!day.colors.includes(colorEvent)) day.colors.push(colorEvent);
            });
        });
    }

    //
    // ────────────────────────────────────────────────────────── I ──────────
    //   :::::: L I F E S T Y L E : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────────
    //

    created() {
        this.calcMatrix(false)
    }

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: M E T H O D S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //

    // ────────────────────────────────────────────────────────────────────────────────
    // event handlers

    handleSelected(day: CalendarDayInfo) {
        this.$emit('input', day.dateValue)
    }

    // ────────────────────────────────────────────────────────────────────────────────
    // calc & render calendar

    /**
     * 달력에 출력할 month 기준 day 를 이차원 배열로 정리
     */
    async calcMatrix(refreshEvent: boolean) {
        let datePickerMatrix = []
        let rows = []
        let today = new Date()
        let daysList = uGetDaysInMonth(this.date, true, 0, false)

        this.matrixStart = daysList[0]
        this.matrixEnd = daysList[daysList.length - 1]

        for (let i = 0; i < daysList.length; i++) {
            let date = daysList[i]

            let isToday =
                date.getFullYear() == today.getFullYear() &&
                date.getMonth() == today.getMonth() &&
                date.getDate() == today.getDate()

            let sDate = uGetDateFormat('yyyy-MM-DD', date)
            let cDate = uGetDateFormat(
                'yyyy-MM-DD',
                IEDate(this.value)
            )

            let dateItem: CalendarDayInfo = {
                date: date,
                dateValue: sDate,
                month: date.getMonth(),
                day: date.getDate(),
                colors: [],
                selected: sDate == cDate,
                isToday: isToday,
                isSun: date.getDay() == 0,
                isSat: date.getDay() == 6,
                isOut:
                date.getMonth() != IEDate(this.value).getMonth(),
            }

            rows.push(dateItem)

            if ((i + 1) % 7 == 0) {
                datePickerMatrix.push(rows)
                rows = []
            }
        }

        this.MonthMatrix = datePickerMatrix

        let strStart = uGetDateFormat('yyyy-MM-dd 00:00:00', this.matrixStart);
        let strEnd = uGetDateFormat('yyyy-MM-dd 00:00:00', this.matrixEnd);

        if (refreshEvent) {
            this.getMonthlyEvents({ start: this.matrixStart, end: this.matrixEnd })
        } else {
            this.onEventsChanged(this.monthlyEvents);
        }
    }

    findColor(folderId: string) {
        return this.folders.find((item) => {
            return item.id
        })?.color
    }

    findColorCode(folderId: string) {
        return this.folders.find((item) => {
            return item.id
        })?.color.code
    }

    changeMonth(count: number) {
        const copyDate = new Date(this.date)
        copyDate.setMonth(copyDate.getMonth() + count)
        this.changeValue(copyDate)
    }

    changeValue(date: Date) {
        this.$emit('input', uGetDateFormat('yyyy-MM-DD', date))
    }  

    createHeaderTitle() {
        const year = this.date.getFullYear()
        const month = this.date.getMonth() + 1

        return `${year}.${month}`
    }

    clearMatrix() {
        for (let week of this.MonthMatrix) {
            for (let day of week) {
                day.colors = [];
            }
        }

    }

    // matrix에서 시작 ~ 종료 기간 사이에 있는 일수를 조회
    findDays(start: Date, end: Date): CalendarDayInfo[] {
        const dates = getDaysArray(start, end).map((x) => uGetDateFormat('yyyy-MM-DD', x));
        const days: CalendarDayInfo[] = []
        for (let week of this.MonthMatrix) {
            for (let day of week) {
                if (dates.includes(day.dateValue)) {
                    days.push(day)
                }
            }
        }
        return days
    }

    findDay(day: Date): CalendarDayInfo | null {
        const target = uGetDateFormat('yyyy-MM-DD', day)
        let foundDay: CalendarDayInfo | null = null;

        for (let week of this.MonthMatrix) {
            for (let day of week) {
                if (day.dateValue === target) {
                    foundDay = day;
                }
            }
        }

        return foundDay;
    }

    isOneDayEvent(item: CalendarEventResponseData) {
        let start = IEDate(item.start);
        let end = IEDate(item.end);
        end = new Date(end.getTime() - 1000);

        if (item.scheduleDuration == '1.00:00:00' && start.toDateString() == end.toDateString()) {
            return true;
        } else {
            return false;
        }
    }
}